/**
 * Database and API Error Translation
 * 
 * Translates technical error codes and messages into user-friendly text.
 * Used by useNotify hook and error handlers throughout the app.
 */

interface PostgresError {
  code?: string;
  message?: string;
  details?: string;
  hint?: string;
}

interface TranslatedError {
  title: string;
  description?: string;
}

// PostgreSQL error codes to user-friendly messages
const postgresErrorMap: Record<string, (details?: string) => TranslatedError> = {
  // NOT NULL violation
  "23502": (details) => {
    // Extract column name from details like "Failing row contains (...null value in column "customer_id"..."
    const columnMatch = details?.match(/column "([^"]+)"/);
    const column = columnMatch ? columnMatch[1].replace(/_/g, " ") : "required field";
    return {
      title: "Missing required information",
      description: `Please fill in the ${column} field before continuing.`,
    };
  },

  // Unique violation (duplicate key)
  "23505": (details) => {
    const keyMatch = details?.match(/Key \(([^)]+)\)/);
    const field = keyMatch ? keyMatch[1].replace(/_/g, " ") : "item";
    return {
      title: "Duplicate entry",
      description: `This ${field} already exists. Please use a different value.`,
    };
  },

  // Foreign key violation
  "23503": () => ({
    title: "Related record not found",
    description: "The referenced item no longer exists. Please refresh and try again.",
  }),

  // Check constraint violation
  "23514": () => ({
    title: "Invalid value",
    description: "The value you entered is not allowed. Please check and try again.",
  }),

  // Permission denied (RLS)
  "42501": () => ({
    title: "Permission denied",
    description: "You don't have permission to perform this action.",
  }),

  // Insufficient privilege
  "42000": () => ({
    title: "Access denied",
    description: "You don't have the required permissions for this operation.",
  }),
};

// PostgREST error codes
const postgrestErrorMap: Record<string, TranslatedError> = {
  PGRST301: {
    title: "Session expired",
    description: "Your session has expired. Please log in again.",
  },
  PGRST302: {
    title: "Not found",
    description: "The requested item could not be found.",
  },
  PGRST116: {
    title: "No results",
    description: "The requested item does not exist or you don't have access to it.",
  },
};

// HTTP status code fallbacks
const httpStatusMap: Record<number, TranslatedError> = {
  400: {
    title: "Invalid request",
    description: "Please check your input and try again.",
  },
  401: {
    title: "Authentication required",
    description: "Please log in to continue.",
  },
  403: {
    title: "Access denied",
    description: "You don't have permission to perform this action.",
  },
  404: {
    title: "Not found",
    description: "The requested item could not be found.",
  },
  409: {
    title: "Conflict",
    description: "This action conflicts with the current state. Please refresh and try again.",
  },
  422: {
    title: "Invalid data",
    description: "The provided data is invalid. Please check and try again.",
  },
  429: {
    title: "Too many requests",
    description: "Please wait a moment before trying again.",
  },
  500: {
    title: "Server error",
    description: "Something went wrong on our end. Please try again later.",
  },
  502: {
    title: "Service unavailable",
    description: "The service is temporarily unavailable. Please try again later.",
  },
  503: {
    title: "Service unavailable",
    description: "The service is temporarily unavailable. Please try again later.",
  },
};

/**
 * Translate a database/API error into user-friendly message
 */
export function translateError(error: unknown): TranslatedError {
  // Handle null/undefined
  if (!error) {
    return {
      title: "An error occurred",
      description: "Please try again.",
    };
  }

  // Handle PostgreSQL errors (from Supabase)
  if (typeof error === "object" && error !== null) {
    const pgError = error as PostgresError;

    // Check for PostgreSQL error code
    if (pgError.code && postgresErrorMap[pgError.code]) {
      return postgresErrorMap[pgError.code](pgError.details);
    }

    // Check for PostgREST error code in message
    if (pgError.message) {
      for (const [code, translation] of Object.entries(postgrestErrorMap)) {
        if (pgError.message.includes(code)) {
          return translation;
        }
      }
    }

    // Check for HTTP status
    if ("status" in error && typeof (error as { status: number }).status === "number") {
      const status = (error as { status: number }).status;
      if (httpStatusMap[status]) {
        return httpStatusMap[status];
      }
    }

    // Fall back to message if available
    if (pgError.message) {
      // Clean up technical messages
      let message = pgError.message;
      
      // Remove PostgreSQL error prefixes
      message = message.replace(/^(ERROR|FATAL|PANIC):\s*/i, "");
      
      // Truncate very long messages
      if (message.length > 200) {
        message = message.substring(0, 200) + "...";
      }

      return {
        title: "Operation failed",
        description: message,
      };
    }
  }

  // Handle Error objects
  if (error instanceof Error) {
    return {
      title: "An error occurred",
      description: error.message || "Please try again.",
    };
  }

  // Handle string errors
  if (typeof error === "string") {
    return {
      title: "An error occurred",
      description: error,
    };
  }

  // Default fallback
  return {
    title: "An error occurred",
    description: "Please try again. If the problem persists, contact support.",
  };
}

/**
 * Check if an error is a network/connectivity issue
 */
export function isNetworkError(error: unknown): boolean {
  if (!error) return false;

  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes("network") ||
      message.includes("fetch") ||
      message.includes("connection") ||
      message.includes("offline") ||
      message.includes("timeout")
    );
  }

  return false;
}

/**
 * Check if an error is an authentication issue
 */
export function isAuthError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  const pgError = error as PostgresError;
  
  // Check error code
  if (pgError.code === "42501" || pgError.code === "42000") {
    return true;
  }

  // Check message for auth-related terms
  if (pgError.message) {
    const message = pgError.message.toLowerCase();
    return (
      message.includes("jwt") ||
      message.includes("auth") ||
      message.includes("session") ||
      message.includes("token") ||
      message.includes("unauthorized")
    );
  }

  return false;
}
